import { PAGE_PATH } from '@/constants';
import { useCouponStore } from '@/store';
import {
  CouponType,
  PreferentialType,
  UserCouponState,
  ValidityPeriodType,
  type AnyObjOrNull,
} from '@/types';
import moment from 'moment';
import NP from 'number-precision';
import { toast } from '../toast';

type CongifsByCouponInfosType = {
  mainText: string; // 主文本
  subText: string; // 副文本， eg：满xx可用
  descriptionText: string; // 辅助副文本的描述文字，eg：最多减xx元
  validityTimeText: string; // 有效时间文本
  _data: AnyObjOrNull; // 特殊场景独自调用
  _isDiscount: boolean; // 是否折扣类型
  _isExchange: boolean; // 是否兑换商品类型
  _isFullSubtraction: boolean; // 是否满减类型
};

/**
 * 获取优惠券有效期信息
 * @param data
 */
const getTextByValidityPeriodType = (data: AnyObjOrNull) => {
  if (!data) return '';

  if (data.type === ValidityPeriodType.validDays) {
    return `自领取起${data.numberOfDays}天有效`;
  }

  if (data.type === ValidityPeriodType.temporalInterval) {
    const start = moment(data.startTime).format('YYYY.MM.DD');
    const end = moment(data.endTime).format('YYYY.MM.DD');
    return `有效期: ${start}-${end}`;
  }

  if (data.type === ValidityPeriodType.longTime) {
    return `永久有效`;
  }

  return '';
};

export default {
  /**
   * 获取优惠券有效的相关渲染信息
   * @param data
   * @returns
   */
  getConfig: (data: AnyObjOrNull): CongifsByCouponInfosType => {
    let config: CongifsByCouponInfosType = {
      mainText: '',
      subText: '',
      descriptionText: '',
      validityTimeText: '',
      _data: data,
      _isDiscount: false,
      _isExchange: false,
      _isFullSubtraction: false,
    };

    if (!data) return config;

    const {
      minimum = 0,
      discount = 0,
      maximumOffer = 0,
      preferentialType,
    } = data.preferentialContent;

    // 是否无门槛
    const isThresholdFree = preferentialType === PreferentialType.thresholdFree;

    config._isDiscount = data.type === CouponType.discount;
    config._isExchange = data.type === CouponType.exchange;
    config._isFullSubtraction = data.type === CouponType.fullSubtraction;

    config.mainText = `${NP.divide(discount ?? 0, 100)}`;

    config.subText = isThresholdFree ? `满任意金额可用` : `满${NP.divide(minimum ?? 0, 100)}可用`;

    config.validityTimeText = getTextByValidityPeriodType(data.validityPeriod);

    if (config._isDiscount) {
      const n = NP.divide(discount ?? 0, 10);
      config = {
        ...config,
        mainText: `${n}`,
        descriptionText: maximumOffer ? `最多减${NP.divide(maximumOffer ?? 0, 100)}元` : '',
      };
    }

    if (config._isExchange) {
      config = {
        ...config,
        mainText: '兑换商品',
      };
    }

    if (config._isFullSubtraction) {
      config = {
        ...config,
        subText: isThresholdFree ? `满任意金额可用` : `满${NP.divide(minimum ?? 0, 100)}可用`,
      };
    }

    return config;
  },
  buttonConfig(data: AnyObject) {
    let config = {
      disabled: false,
      onClick: () => {
        console.log(this.getConfig(data.coupon));
        // 缓存渲染信息
        useCouponStore().couponConfigCache = this.getConfig(data.coupon);
        uni.navigateTo({ url: PAGE_PATH.couponSuit.route });
      },
    };

    if (data.state === UserCouponState.notStarted) {
      return {
        ...config,
        disabled: true,
        onClick: () => toast.message(`优惠券未开始`),
      };
    }

    if (data.state === UserCouponState.haveUsed) {
      return {
        ...config,
        disabled: true,
        onClick: () => toast.message(`优惠券已失效`),
      };
    }

    if (data.state === UserCouponState.expire) {
      return {
        ...config,
        onClick: () => toast.message(`优惠券已失效`),
      };
    }

    return config;
  },
};
